/* Mantenimiento: preguntas y opciones de la evaluación de un tema de Capacitaciones.
 *
 * Se guarda la lista completa. Una pregunta que alguien ya respondió no se
 * pisa: si se modifica, el backend la desactiva y crea una nueva (el historial
 * de respuestas queda como se respondió); si se quita, solo se desactiva.
 */
import { IonButton, IonContent, IonIcon, IonPage, useIonToast } from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import { addOutline, arrowBack, checkmark, informationCircleOutline, trashOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "src/components/Header";
import RegistroCapacitacionAPI, { PreguntaAdmin, Tema, etiquetaTipo } from "src/services/registroCapacitacion";
import { tipoDesdeRuta } from "./CapacitacionMantenimiento";
import "./CapacitacionAdm.css";

const MAX_PREGUNTAS = 20;
const MAX_OPCIONES = 6;

// Claves estables para las listas editables (con el índice como key, al borrar
// un elemento React reutiliza el input del siguiente y el texto "salta").
let ultimaClave = 0;
const nuevaClave = () => ++ultimaClave;

interface OpcionForm {
  _k: number;
  Opcion: string;
  RespuestaCorrecta: boolean;
}

interface PreguntaForm {
  _k: number;
  Id?: number;
  Respondida?: boolean;
  Pregunta: string;
  Opciones: OpcionForm[];
}

const nuevaOpcion = (correcta = false): OpcionForm => ({ _k: nuevaClave(), Opcion: "", RespuestaCorrecta: correcta });

const nuevaPregunta = (): PreguntaForm => ({
  _k: nuevaClave(),
  Pregunta: "",
  Opciones: [nuevaOpcion(true), nuevaOpcion(), nuevaOpcion()],
});

const conClaves = (preguntas: PreguntaAdmin[]): PreguntaForm[] =>
  preguntas.map((p) => ({
    _k: nuevaClave(),
    Id: p.Id,
    Respondida: p.Respondida,
    Pregunta: p.Pregunta,
    Opciones: p.Opciones.map((o) => ({ _k: nuevaClave(), Opcion: o.Opcion, RespuestaCorrecta: o.RespuestaCorrecta })),
  }));

const CapacitacionTemaEvaluacion: React.FC = () => {
  const { tipo: tipoRuta, id } = useParams<{ tipo: string; id: string }>();
  const tipo = tipoDesdeRuta(tipoRuta);
  const temaId = Number(id);
  const history = useHistory();
  const [present] = useIonToast();

  const [tema, setTema] = useState<Tema | null>(null);
  const [preguntas, setPreguntas] = useState<PreguntaForm[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const volver = () => history.push(`/capacitaciones/mantenimiento/${(tipo || "").toLowerCase()}`);

  const cargar = async () => {
    setCargando(true);
    try {
      const datos = await RegistroCapacitacionAPI.adminPreguntas(temaId);
      setTema(datos?.Tema || null);
      setPreguntas(conClaves(datos?.Preguntas || []));
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo cargar la evaluación", duration: 4000, color: "danger" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temaId]);

  const editarPregunta = (pi: number, cambio: (p: PreguntaForm) => PreguntaForm) =>
    setPreguntas((prev) => prev.map((p, i) => (i === pi ? cambio(p) : p)));

  const editarOpcion = (pi: number, oi: number, texto: string) =>
    editarPregunta(pi, (p) => ({ ...p, Opciones: p.Opciones.map((o, i) => (i === oi ? { ...o, Opcion: texto } : o)) }));

  const marcarCorrecta = (pi: number, oi: number) =>
    editarPregunta(pi, (p) => ({ ...p, Opciones: p.Opciones.map((o, i) => ({ ...o, RespuestaCorrecta: i === oi })) }));

  const agregarOpcion = (pi: number) =>
    editarPregunta(pi, (p) => ({ ...p, Opciones: [...p.Opciones, nuevaOpcion()] }));

  const quitarOpcion = (pi: number, oi: number) =>
    editarPregunta(pi, (p) => {
      const quitada = p.Opciones[oi];
      const resto = p.Opciones.filter((_, i) => i !== oi);
      // Si se quitó la correcta, la primera pasa a ser la correcta.
      if (quitada?.RespuestaCorrecta && resto.length) resto[0] = { ...resto[0], RespuestaCorrecta: true };
      return { ...p, Opciones: resto };
    });

  const validar = (): string | null => {
    for (let i = 0; i < preguntas.length; i++) {
      const p = preguntas[i];
      if (!p.Pregunta.trim()) return `Completá el texto de la pregunta ${i + 1}`;
      const llenas = p.Opciones.filter((o) => o.Opcion.trim());
      if (llenas.length < 2) return `La pregunta ${i + 1} necesita al menos 2 opciones`;
      if (!llenas.some((o) => o.RespuestaCorrecta)) return `Marcá cuál es la opción correcta de la pregunta ${i + 1}`;
    }
    return null;
  };

  const guardar = async () => {
    const error = validar();
    if (error) {
      present({ message: error, duration: 3500, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      await RegistroCapacitacionAPI.adminGuardarPreguntas(
        temaId,
        preguntas.map((p) => ({
          Id: p.Id,
          Pregunta: p.Pregunta.trim(),
          Opciones: p.Opciones.filter((o) => o.Opcion.trim()).map((o) => ({
            Opcion: o.Opcion.trim(),
            RespuestaCorrecta: o.RespuestaCorrecta,
          })),
        }))
      );
      present({ message: "Evaluación guardada", duration: 2500, color: "success" });
      // Recargar: las preguntas reemplazadas vuelven con Id nuevo.
      cargar();
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo guardar la evaluación", duration: 4000, color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  const hayRespondidas = preguntas.some((p) => p.Respondida);

  return (
    <IonPage>
      <Header titulo={`Evaluación · Capacitación ${etiquetaTipo(tipo || "")}`} />
      <IonContent id="capacitacion-tema-evaluacion">
        <div className="capadm-page capadm-scope campos-mui">
          <IonButton fill="clear" size="small" onClick={volver} style={{ marginLeft: -8 }}>
            <IonIcon slot="start" icon={arrowBack} /> Volver a los temas
          </IonButton>

          {cargando ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : !tema ? (
            <div className="capadm-vacio">No se encontró el tema.</div>
          ) : (
            <>
              <div className="capadm-card">
                <p className="capadm-card-titulo">{tema.Tema}</p>
                <div className="capadm-meta">
                  <span>{tema.Categoria}</span>
                  <span>{tema.Horas} h</span>
                  <span>{tema.Participantes}</span>
                </div>
              </div>

              <div className="capadm-card">
                <p className="capadm-card-titulo">
                  Preguntas {preguntas.length > 0 ? `(${preguntas.length})` : ""}
                </p>
                <p className="capadm-card-sub">
                  Las recibe cada capacitado al terminar la capacitación. Tocá el ✓ para marcar la opción correcta.
                  Sin preguntas, el tema no tiene evaluación.
                </p>
                {hayRespondidas && (
                  <div className="capadm-aviso info" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <IonIcon icon={informationCircleOutline} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      Las preguntas marcadas como <strong>ya respondidas</strong> se pueden cambiar: se guardan como
                      una pregunta nueva y la anterior queda en el historial de quienes ya la respondieron.
                    </span>
                  </div>
                )}

                {preguntas.map((p, pi) => (
                  <div key={p._k} className="capadm-pregunta">
                    <div className="capadm-pregunta-header">
                      <span>
                        Pregunta {pi + 1}
                        {p.Respondida && (
                          <span style={{ fontWeight: 400, color: "#2563eb", marginLeft: 8 }}>· ya respondida</span>
                        )}
                      </span>
                      <IonButton
                        fill="clear"
                        size="small"
                        color="danger"
                        onClick={() => setPreguntas((prev) => prev.filter((_, i) => i !== pi))}
                      >
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    </div>
                    <TextField
                      variant="outlined"
                      fullWidth
                      multiline
                      value={p.Pregunta}
                      placeholder="Escribí la pregunta"
                      inputProps={{ maxLength: 500 }}
                      onChange={(e) => {
                        const texto = e.target.value;
                        editarPregunta(pi, (q) => ({ ...q, Pregunta: texto }));
                      }}
                    />
                    <div style={{ marginTop: 10 }}>
                      {p.Opciones.map((o, oi) => (
                        <div key={o._k} className="capadm-opcion">
                          <button
                            type="button"
                            className={`capadm-correcta${o.RespuestaCorrecta ? " activa" : ""}`}
                            onClick={() => marcarCorrecta(pi, oi)}
                            aria-label={o.RespuestaCorrecta ? "Opción correcta" : "Marcar como correcta"}
                            title={o.RespuestaCorrecta ? "Opción correcta" : "Marcar como correcta"}
                          >
                            <IonIcon icon={checkmark} />
                          </button>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={o.Opcion}
                            placeholder={`Opción ${String.fromCharCode(65 + oi)}`}
                            inputProps={{ maxLength: 300 }}
                            onChange={(e) => editarOpcion(pi, oi, e.target.value)}
                          />
                          {p.Opciones.length > 2 && (
                            <IonButton fill="clear" size="small" color="medium" onClick={() => quitarOpcion(pi, oi)}>
                              <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonButton>
                          )}
                        </div>
                      ))}
                    </div>
                    {p.Opciones.length < MAX_OPCIONES && (
                      <IonButton fill="clear" size="small" onClick={() => agregarOpcion(pi)}>
                        <IonIcon slot="start" icon={addOutline} /> Agregar opción
                      </IonButton>
                    )}
                  </div>
                ))}

                {preguntas.length < MAX_PREGUNTAS && (
                  <IonButton
                    expand="block"
                    fill="outline"
                    onClick={() => setPreguntas((prev) => [...prev, nuevaPregunta()])}
                  >
                    <IonIcon slot="start" icon={addOutline} /> Agregar pregunta
                  </IonButton>
                )}
              </div>

              <IonButton expand="block" onClick={guardar} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar evaluación"}
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CapacitacionTemaEvaluacion;
